<template>
  <div class="min-h-screen bg-zinc-50">
    <div class="mx-auto max-w-3xl px-6 py-12">
      <!-- Logo -->
      <div class="flex justify-center">
        <img :src="require('@/assets/images/logo-header.webp')" class="h-20" />
      </div>

      <!-- Success -->
      <div class="mt-10 text-center">
        <div
          class="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-green-100"
        >
          <svg
            class="h-14 w-14 text-green-600"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h1 class="mt-6 text-4xl font-bold text-secondary">
          Payment Confirmed
        </h1>

        <p class="mt-4 text-lg text-zinc-600">
          Thank you for choosing
          <span class="font-semibold text-orange-500"> IWC HMO </span>
          as your trusted healthcare provider.
        </p>

        <p class="mt-3 text-zinc-500">
          Your payment has been successfully received, and your membership
          renewal request will be processed.
        </p>
      </div>

      <!-- Receipt -->
      <div
        class="mt-10 rounded-3xl border border-zinc-200 bg-white p-8 shadow-sm"
      >
        <h2 class="mb-8 text-xl font-semibold text-secondary">
          Payment Acknowledgment
        </h2>

        <div class="space-y-6">
          <div class="flex justify-between">
            <span class="text-zinc-500">Reference Number</span>
            <span class="font-semibold">
              {{ $store.state.planDetails.referenceNumber }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-zinc-500">Amount</span>

            <span class="px-3 py-1 text-sm font-semibold">{{
              $store.state.selectedPaymentOption.selectedPaymentOption ===
              "one-time"
                ? amountPaymentChannelFormat($store.state.planDetails.premium)
                : amountPaymentChannelFormat(
                    $store.state.selectedInstallment.amountToPay
                  )
            }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-zinc-500">Payment Status</span>

            <span
              class="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-700"
              >Processing
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-zinc-500">Payment Channel</span>

            <span class="px-3 py-1 text-sm font-semibold"
              >G-Cash (09274468935)</span
            >
          </div>
          <div class="flex justify-between">
            <span class="text-zinc-500">Email Address</span>
            <span class="font-semibold"> juandelacruz@mail.com </span>
          </div>

          <div class="flex justify-between">
            <span class="text-zinc-500">Payment Date</span>
            <span class="font-semibold"> {{ dateFormatMonthDateYear() }} </span>
          </div>
        </div>
      </div>

      <!-- Information -->
      <!-- What Happens Next -->
      <div class="mt-8 rounded-3xl border border-orange-200 bg-orange-50 p-8">
        <div class="flex items-center gap-3">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-orange-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"
              />
            </svg>
          </div>

          <h2 class="text-xl font-semibold text-secondary">
            What Happens Next?
          </h2>
        </div>

        <div class="mt-6 space-y-5">
          <div class="flex items-start gap-4">
            <div
              class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-orange-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="3"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <p class="text-zinc-700">
              We have successfully received your payment.
            </p>
          </div>

          <div class="flex items-start gap-4">
            <div
              class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-orange-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="3"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <p class="text-zinc-700">
              Your membership renewal request will be processed and validated.
            </p>
          </div>

          <div
            class="flex items-start gap-4"
            v-if="
              $store.state.selectedPaymentOption.selectedPaymentOption ===
              'installment'
            "
          >
            <div
              class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-orange-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="3"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <p class="leading-7 text-zinc-700">
              For installment payments, an
              <span class="font-semibold text-secondary">
                Acknowledgment Receipt
              </span>
              will be available for download on the
              <span class="font-semibold text-secondary">
                Tracking Status
              </span>
              page using your
              <span class="font-semibold text-secondary">
                Reference Number
              </span>
              once your payment has been verified.
              <br /><br />
              Once your renewal has been
              <span class="font-semibold text-secondary"> fully paid </span>
              and successfully completed, your
              <span class="font-semibold text-secondary">
                Virtual Service Invoice
              </span>
              will be sent to your
              <span class="font-semibold text-secondary">
                registered email address
              </span>
              and will also be available for download on the
              <span class="font-semibold text-secondary">
                Tracking Status
              </span>
              page using your
              <span class="font-semibold text-secondary">
                Reference Number.
              </span>
            </p>
          </div>
          <div v-else class="flex items-start gap-4">
            <div
              class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-orange-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="3"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <p class="text-zinc-700 leading-7">
              Once your membership renewal has been completed, your
              <span class="font-semibold text-secondary">
                Virtual Service Invoice
              </span>
              will be sent to your
              <span class="font-semibold text-secondary">
                registered email address
              </span>
              and will also be available for download on the
              <span class="font-semibold text-secondary">
                Tracking Status
              </span>
              page using your
              <span class="font-semibold text-secondary">
                Reference Number.
              </span>
            </p>
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div class="mt-10 flex flex-col gap-4 sm:flex-row">
        <button
          @click="downloadReceipt()"
          class="flex-1 rounded-xl border border-zinc-300 py-3 font-semibold hover:bg-zinc-100"
        >
          Download Receipt
        </button>

        <router-link
          :to="`/payment/center/tracking/${$store.state.planDetails.referenceNumber}`"
          class="flex-1 rounded-xl bg-orange-500 py-3 text-center font-semibold text-white transition hover:bg-orange-600"
        >
          Tracking Details
        </router-link>
      </div>

      <!-- Footer -->
      <div class="mt-12 text-center">
        <p class="font-medium text-secondary">Need Assistance?</p>

        <p class="mt-2 text-zinc-500">
          Please contact the IWC HMO Customer Support Team if you have any
          questions regarding your payment or membership renewal.
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import reusableBase from "@/utils/composables/format";

export default {
  name: "PaymentSuccess",

  setup() {
    const { amountPaymentChannelFormat, dateFormatMonthDateYear } =
      reusableBase();

    const downloadReceipt = () => {
      const link = document.createElement("a");
      link.href = "/Payment_Receipt.pdf";
      link.download = "Payment_Receipt.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return {
      amountPaymentChannelFormat,
      dateFormatMonthDateYear,
      downloadReceipt,
    };
  },
};
</script>
